import { Sim } from '../game/simulation';
import { PLOT_W, PLOT_H } from '../game/simulation/types';
import { SoundBank, MixLayer, PlayOpts, CueOpts, GroupConfig } from './SoundBank';
import { AmbientMixer } from './AmbientMixer';
import { PositionalEmitter } from './PositionalEmitter';
import type { Settings } from './Settings';

/** Minimal listener-state accessor used by Sounds to drive 3D audio.
 *  Decoupled from the Diorama class so tests can stub it. */
interface ListenerProvider {
  getListenerState(): { x: number; z: number; zoom: number };
  getTruckState(): { x: number; z: number; isMoving: boolean } | null;
}

/**
 * Hybrid SFX layer.
 *
 *   1. SoundBank-first: AudioBuffer variation pools registered via
 *      `bank.load(group, [files])`. Played with per-call pitch / volume /
 *      delay — this is the substrate for layered sound design.
 *   2. Legacy HTMLAudioElement samples: pre-loaded via `loadSamples([...])`
 *      and played by `play(name)`. Kept for back-compat; new sounds should
 *      use the bank.
 *   3. Synth fallback: if neither sample path triggers, the original
 *      Web-Audio synthesised cue fires so the game is never silent while
 *      the player curates real audio.
 *
 * Files live in `public/sounds/`. Variation pools are registered in
 * `registerSoundGroups()` below — add files there to register new groups.
 */

/**
 * Variation pools — each key is a logical sound. Each entry holds:
 *   - `files`     list of audio basenames in `public/sounds/`. Bare
 *                 names default to `.mp3`; include an extension
 *                 (.wav / .ogg / .m4a / .flac / .opus / .aac) if the
 *                 file is in another format. Uppbeat WAVs work directly.
 *   - `defaults`  per-group baseline modulation: volume, pitch base,
 *                 random pitch jitter, random pan jitter. These are
 *                 applied to every play of the group BEFORE the
 *                 caller's per-play opts.
 *
 * Add new groups here as you curate more clips. The bank loads them all
 * in parallel on AudioContext unlock.
 */
/**
 * Action-based group registry.
 *
 * Each group name corresponds to a folder under `public/sounds/`. The
 * folder structure mirrors gameplay actions: each top-level folder is
 * an action (chip-land, hoe-till, rock-break, ...) and inside it are
 * subfolders for the layers (impact, scrape, debris, ...).
 *
 * Group names use the path-style `action/layer` convention so it's
 * clear from the code which action and which layer a group serves.
 * The `dir` field maps to the on-disk path (defaults to group name).
 *
 * Adding a new action: create the folder under public/sounds/, drop
 * an INSTRUCTIONS.md, register groups here, compose a cue in
 * defineCues(), wire the bus event. See public/sounds/README.md for
 * the full recipe.
 */
/**
 * Group → file mapping. Each entry pulls the listed clips from
 * `public/sounds/<group-or-dir>/` and applies the per-group defaults
 * to every play.
 *
 * The `maxLengthMs` values come from a sound-engineering audit of the
 * curated library. Rules of thumb:
 *   - drag-paint single-fire layers (subCellTilled, subCellHarvested):
 *     ≤200ms — otherwise overlapping fires turn into mush
 *   - one-shot tile completions (chop, rock-break): 350-500ms — long
 *     enough to read as "the thing happened", short enough not to
 *     block the next action
 *   - environment / continuous loops (truck/motor, forest, wind):
 *     NO cap — let the full clip play and the ambient mixer / motor
 *     emitter handles looping & fades
 *   - UI cues: 150-400ms for crisp denial/hint blips; bells (chime,
 *     level-up, building/constructed) get no cap so the decay can
 *     ring out naturally
 *
 * `dir:` overrides the on-disk folder when the group name doesn't
 * match the actual folder layout (the user populated `hoe-tap/`
 * directly rather than the layered `hoe-till/{impact,scrape}/`
 * sub-folders).
 */
const SOUND_GROUPS: Record<string, GroupConfig> = {
  // ── chip-land action — HUD chip receives a flying item.
  // Source files are ~2.0s but the actual pop is in the first 150ms;
  // cap it to keep rapid harvest bursts crisp instead of layering tails.
  'chip-land/pop': {
    files: [
      'bubble-pop-bright-om-fx-1-1-00-02',
      'bubble-pop-bright-om-fx-2-2-00-02',
      'bubble-pop-bright-om-fx-3-3-00-02',
      'bubble-pop-higher-om-fx-1-1-00-01',
      'bubble-pop-higher-om-fx-2-2-00-02',
      'bubble-pop-low-om-fx-3-3-00-02',
      'bubble-pop-medium-om-fx-1-1-00-02',
      'bubble-pop-medium-om-fx-2-2-00-02',
      'bubble-pop-medium-om-fx-3-3-00-02',
      'bubble-pop-medium-om-fx-4-4-00-01',
    ],
    defaults: { volume: 0.7, pitchJitter: 0.05, panJitter: 0.15, maxLengthMs: 180 },
  },

  // ── crop-cut — sickle swish + plant snip layered before the pop
  // (defined in `chip-land/pop`). 250ms cap on the swish so a drag-
  // paint sickle doesn't smear; 150ms cap on the snip because the
  // scissors-snipping clip has a long swoop tail that doesn't belong.
  'crop-cut/swish': {
    files: [
      'Single_fast_whoosh_o_#1-1779039661016.wav',
      'air-swish-jam-fx-2-2-00-00',
      'swish-swift-cut-jam-fx-1-00-00',
      'whoosh-stick-wiff-volo-1-1-00-00',
    ],
    defaults: { volume: 0.55, pitchJitter: 0.08, panJitter: 0.10, maxLengthMs: 250 },
  },
  'crop-cut/snip': {
    files: [
      'A_single_crisp_snip__#1-1779039586004.wav',
      'scissors-snipping-slowly-swoop-1-00-01',
    ],
    defaults: { volume: 0.45, pitchJitter: 0.08, panJitter: 0.10, maxLengthMs: 150 },
  },

  // ── chop-wood — attack thud + splinter detail. fell is the rare
  // big-deal cue when the tree actually drops; everything else is
  // per-axe-strike. The 'metal axe' source clips violate the prompt
  // and read metallic — wood-smash variant is the on-prompt anchor.
  'chop-wood/impact': {
    files: [
      'axe-hitting-metal-smartsound-fx-hard-1-00-00',
      'metal-axe-impacts-tree-stump-smartsound-fx-1-00-00',
      'wood-smash-impact-joshua-chivers-2-2-00-00',
    ],
    defaults: { volume: 0.8, pitchJitter: 0.06, panJitter: 0.12, maxLengthMs: 400 },
    dir: 'chop-wood/impact',
  },
  'chop-wood/crack': {
    files: [
      'wood-crack-mechanical-wave-1-00-00',
      'wood-crack-short-mechanical-wave-1-00-00',
    ],
    defaults: { volume: 0.5, pitchJitter: 0.08, panJitter: 0.10, maxLengthMs: 500 },
    dir: 'chop-wood/crack',
  },
  'chop-wood/fell': {
    files: ['tree-falling-in-forest-joshua-chivers-1-00-07'],
    defaults: { volume: 0.65, pitchJitter: 0.03, panJitter: 0.05 },
    dir: 'chop-wood/crack',     // the 7.3s fall clip lives in the crack folder
  },

  // ── hoe-till — the user dropped files into `hoe-tap/` directly
  // instead of `hoe-till/impact|scrape`. Both groups read from
  // `hoe-tap/` via `dir:`. Tight 150ms cap on impact (drag-paint
  // single tap), 220ms on scrape (one footstep's worth of grain).
  'hoe-till/impact': {
    files: [
      'shovel-in-dirt-epic-stock-media-1-00-00',
      'shoveling-dirt-epic-stock-media-1-00-00',
    ],
    defaults: { volume: 0.65, pitchJitter: 0.08, panJitter: 0.10, maxLengthMs: 150 },
    dir: 'hoe-tap',
  },
  'hoe-till/scrape': {
    files: ['footsteps-walking-on-dirt-crocs-slippers-vadi-sound-1-00-02'],
    defaults: { volume: 0.45, pitchJitter: 0.08, panJitter: 0.10, maxLengthMs: 220 },
    dir: 'hoe-tap',
  },

  // ── rock-break — pickaxe ringings (3 variants) + a 4th heavier
  // borrowed from stone-deposit/. Debris is the dust-scatter detail.
  'rock-break/impact': {
    files: [
      'pickaxe-impacting-rock-gamemaster-audio-1-1-00-00',
      'pickaxe-impacting-rock-gamemaster-audio-2-2-00-00',
      'pickaxe-impacting-rock-gamemaster-audio-3-3-00-00',
    ],
    defaults: { volume: 0.8, pitchJitter: 0.06, panJitter: 0.12, maxLengthMs: 350 },
    dir: 'rock-break/impact',
  },
  'rock-break/debris': {
    files: ['boom-deep-thud-with-debris-dust-gregor-quendel-1-00-01'],
    defaults: { volume: 0.5, pitchJitter: 0.08, panJitter: 0.15, maxLengthMs: 600 },
    dir: 'rock-break/debris',
  },
  /** Heavier stone-impact pool used for stone-deposit cue + as a 4th
   *  rock-break/impact variation. Kept as a separate group so its
   *  longer body doesn't smear the drag-paint pickaxe cadence. */
  'stone/heavy': {
    files: [
      'rock-impact-small-smartsound-fx-1-00-02',
      'rock-impact-trap-closing-gamemaster-audio-2-2-00-00',
    ],
    defaults: { volume: 0.75, pitchJitter: 0.06, panJitter: 0.10, maxLengthMs: 400 },
    dir: 'stone-deposit',
  },

  // ── building cues.
  // place    — first-frame thunk when player drops a footprint.
  // raising  — under-construction "in progress" pulses (knock + drill).
  // constructed — bright chime when the build finishes.
  'building/place': {
    files: ['hammer-placed-on-workbench-tomas-herudek-1-00-00'],
    defaults: { volume: 0.75, pitchJitter: 0.05, panJitter: 0.08, maxLengthMs: 450 },
    dir: 'building/place',
  },
  'building/raising': {
    files: [
      'knocking-on-wood-fascinatedsound-1-00-01',
      'workshop-electric-drill-pulsing-soundroll-2-2-00-01',
      'workshop-electric-drill-pulsing-soundroll-3-3-00-01',
    ],
    defaults: { volume: 0.55, pitchJitter: 0.06, panJitter: 0.10, maxLengthMs: 700 },
    dir: 'building/raising',
  },
  'building/constructed': {
    files: [
      'notification-bright-positive-chime-gfx-sounds-1-1-00-01',
      'notification-bright-positive-chime-gfx-sounds-2-2-00-02',
    ],
    defaults: { volume: 0.75, pitchJitter: 0.03, panJitter: 0.05 },
    dir: 'building/constructed',
  },
  /** Borrowed rubble file moved here for the rock-break debris layer
   *  too — single file pulling double duty. */
  'building/rubble': {
    files: ['impact-rubble-smashed-epic-stock-media-1-00-02'],
    defaults: { volume: 0.55, pitchJitter: 0.07, panJitter: 0.15, maxLengthMs: 700 },
    dir: 'building/constructed',
  },

  // ── truck — motor is a looped emitter (no cap); horns, brake, tires
  // are short one-shots; load is a thump on cargo deposit.
  'truck/motor': {
    files: ['truck-engine-idling-fascinatedsound-1-00-18'],
    defaults: { volume: 0.4, pitchJitter: 0.02, panJitter: 0 },
    dir: 'truck/motor',
  },
  'truck/tires': {
    files: [
      'car-skidding-tire-squeal-gfx-sounds-5-5-00-05',
      'car-squealing-tires-fascinatedsound-1-00-05',
    ],
    defaults: { volume: 0.28, pitchJitter: 0.05, panJitter: 0.10, maxLengthMs: 700 },
    dir: 'truck/tires',
  },
  'truck/horn': {
    files: [
      'car-horn-beep-twice-sonic-bat-1-00-01',
      'car-horn-double-honk-sonic-bat-1-00-01',
    ],
    defaults: { volume: 0.55, pitchJitter: 0.04, panJitter: 0.05, maxLengthMs: 600 },
    dir: 'truck/horn',
  },
  'truck/brake': {
    files: ['car-tires-screech-heavy-braking-bosnow-1-00-03'],
    defaults: { volume: 0.35, pitchJitter: 0.04, panJitter: 0, maxLengthMs: 700 },
    dir: 'truck/brake',
  },
  'truck/load': {
    files: [
      'Solid_satisfying_thu_#1-1779040195409.wav',
      'Solid_satisfying_thu_#2-1779040180226.wav',
      'wood-smash-impact-joshua-chivers-2-2-00-00',
    ],
    defaults: { volume: 0.7, pitchJitter: 0.06, panJitter: 0.10, maxLengthMs: 450 },
    dir: 'truck/load',
  },
  /** Engine wind-down / wind-up shared between arrive and depart cues
   *  (same file — depart will be replaced with a proper "rolling away"
   *  clip later). */
  'truck/engine-stop': {
    files: ['truck-engine-shut-off-epic-stock-media-1-00-02'],
    defaults: { volume: 0.5, pitchJitter: 0.02, panJitter: 0 },
    dir: 'truck/arrive',
  },

  // ── UI — short, dry, polite. Caps on deny/hint keep them crisp.
  'ui/deny': {
    files: [
      'Short_soft_polite_UI_#3-1779039756762.wav',
      'Short_soft_polite_UI_#4-1779039763513.wav',
      'mouse-click-access-denied-jam-fx-1-00-00',
      'mouse-click-negative-click-jam-fx-1-00-00',
    ],
    defaults: { volume: 0.55, pitchJitter: 0.04, panJitter: 0, maxLengthMs: 400 },
  },
  'ui/hint': {
    files: [
      'Tiny_soft_UI_blip_—__#1-1779039864678.wav',
      'notification-gentle-confirmation-gfx-sounds-1-00-00',
    ],
    defaults: { volume: 0.4, pitchJitter: 0.03, panJitter: 0, maxLengthMs: 150 },
  },
  'ui/chime': {
    files: ['Brief_positive_notif_#4-1779039795762.wav'],
    defaults: { volume: 0.55 },
  },
  /** The previous SOUND_GROUPS pointed at a file that no longer exists
   *  on disk (Short_triumphant_asc_#1-*.wav). Replaced with the
   *  notification-success file the user actually curated. */
  'ui/level-up': {
    files: ['notification-success-chime-gfx-sounds-1-00-02'],
    defaults: { volume: 0.7, pitchJitter: 0.02, panJitter: 0 },
  },

  // ── Environment / forest — looping daytime woodland bed. Mixed
  // sources: two long Uppbeat MP3s (81s + 142s) plus two ElevenLabs
  // 2-second wavs (loop-seam risky — deprioritized). Mixer crossfades
  // a fresh pick every clip-length. Volume here is full; actual
  // playback level is shaped by driveAmbientState (loud at noon,
  // silent at midnight, ducked 40% in plot mode).
  'environment/forest': {
    files: [
      'birdsong-insects-on-summer-day-epic-stock-media-1-01-21',
      'mountain-forest-ambience-spruce-trees-blowing-birds-crickets-ivo-vicic-1-02-22',
      'Continuous_daytime_w_#1-1779040131059.wav',
      'Continuous_daytime_w_#4-1779040118290.wav',
    ],
    defaults: { volume: 1.0 },
  },

  // ── Environment / birds — scheduled one-shots, NOT a bed.
  'environment/birds': {
    files: [
      'great-grey-owl-calling-smartsound-fx-1-1-00-01',
      'great-grey-owl-calling-smartsound-fx-2-2-00-01',
      'parrot-whistles-smartsound-fx-1-00-00',
      'small-bird-chirping-distressed-gamemaster-audio-1-00-01',
      'snowy-owl-tweet-call-smartsound-fx-1-1-00-01',
      'snowy-owl-tweet-call-smartsound-fx-2-2-00-01',
    ],
    defaults: { volume: 0.22, pitchJitter: 0.08, panJitter: 0.6 },
  },

  // ── Environment / crickets — night-only insect chorus, driven by
  // driveAmbientState's nightCurve.
  'environment/crickets': {
    files: [
      'awkward-silence-crickets-soundroll-1-00-09',
      'cricket-chirping-epic-stock-media-1-1-00-03',
      'cricket-chirping-epic-stock-media-2-2-00-06',
    ],
    defaults: { volume: 1.0 },
  },

  // ── Environment / wind — always-on bed at quiet volume. The
  // "howling snowstorm" file violates the brief; only the long
  // wind-in-trees file is used as a bed.
  'environment/wind': {
    files: ['wind-blowing-trees-in-woods-ivo-vicic-1-02-49'],
    defaults: { volume: 1.0 },
  },

  // ── Environment / rain & thunder — single files each, gated by a
  // future weather state. Not yet driven (placeholder beds).
  'environment/rain': {
    files: ['rain-ambience-heavy-downpour-vincentmets-1-01-23'],
    defaults: { volume: 1.0 },
  },
  'environment/thunder': {
    files: [
      'distant-thunder-roll-jeff-kaale-1-1-00-06',
      'distant-thunder-roll-jeff-kaale-2-2-00-08',
    ],
    defaults: { volume: 0.45, pitchJitter: 0.04, panJitter: 0.3 },
  },
};

/** Per-resource pop pitch — the harvested/landed item's "size". Low pitch
 *  = big plonk (wood, stone), high pitch = small pop (money, flour). */
const POP_PITCH: Record<string, number> = {
  money:      1.45,
  basket:     1.10,
  corn:       1.05,
  carrot:     0.95,
  flour:      1.25,
  cornFlour:  1.20,
  wood:       0.65,
  stone:      0.55,
  mushroom:   0.85,
  berry:      1.15,
  treeSeeds:  0.75,
};

export class Sounds {
  private ctx: AudioContext | null = null;
  /** Variation-pool layer — preferred path for new sounds. Built once
   *  on AudioContext unlock; groups are loaded in parallel. */
  private bank: SoundBank | null = null;
  /** Pre-loaded sample audio. Each entry is the "master" — `play()` clones it
   *  so overlapping fires don't cut each other off. */
  private samples = new Map<string, HTMLAudioElement>();
  /** Names we tried to load but failed (404 / decode error). We don't retry. */
  private failed = new Set<string>();
  /** Last-play timestamp per cue name. Used to throttle high-frequency
   *  drag-paint events (sub-cell hoe / seed / water / sickle) so a single
   *  drag stroke doesn't fire 20 rapid pops. */
  private lastPlayAt = new Map<string, number>();

  /** Sim reference kept on the instance so the ambient bird scheduler
   *  can check `getTimeOfDay` to gate calls to daylight hours. */
  private sim!: Sim;
  /** Reentry guard for the bird scheduler — if Sounds were ever
   *  instantiated twice (HMR), we don't want two timers stacked. */
  private birdSchedulerStarted = false;
  /** Continuous-loop ambient subsystem — separate audio pathway from
   *  one-shot effects. Beds (forest, wind, crickets, rain) play
   *  simultaneously at independent volumes, crossfaded for seamless
   *  looping. Driven by sim.getTimeOfDay() in the ambient update loop. */
  private ambient: AmbientMixer | null = null;
  /** Reentry guard for the ambient update loop. */
  private ambientLoopStarted = false;
  /** Last rAF timestamp — used to compute dt for the ambient mixer. */
  private ambientLastTs = 0;
  /** Persistent positional emitters — looping sources whose world
   *  position updates each frame (truck motor while driving, etc.).
   *  Distinct from one-shot positional plays (fixed pan at fire time)
   *  and ambient beds (no position at all). */
  private emitters: PositionalEmitter | null = null;
  /** External listener-state provider — the Diorama. Set by main.ts
   *  via `connectListener()` after Diorama has been constructed.
   *  Drives the per-frame listener position + truck motor emitter
   *  position. Null until connected; positional plays before that
   *  point default to listener at world origin. */
  private listener: ListenerProvider | null = null;
  /** Settings store — drives master volume + mute. Connected by
   *  main.ts after both Sounds and Settings exist. Null until
   *  connected; in that state the bank's default master gain stays
   *  in effect (so audio works even if settings hookup is omitted). */
  private settings: Settings | null = null;
  /** Unsubscribe handle for the settings listener — kept so we don't
   *  leak subscriptions if connectSettings is somehow called twice. */
  private settingsUnsub: (() => void) | null = null;

  constructor(sim: Sim) {
    this.sim = sim;
    const unlock = () => {
      if (!this.ctx) {
        this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        this.bank = new SoundBank(this.ctx);
        this.ambient = new AmbientMixer(this.bank);
        this.emitters = new PositionalEmitter(this.bank);
        this.registerSoundGroups();
        this.startBirdScheduler();
        this.startAmbientLoop();
        // Settings was likely connected before the bank existed (since
        // main.ts wires it pre-unlock); re-apply now that the bank can
        // accept setMasterVolume.
        this.applySettingsVolume();
      }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    sim.bus.on(e => {
      switch (e.type) {
        // ── Tile-level work — every cue carries the world position of
        // its tile so the spatial chain (distance gain + pan + air-
        // absorption lowpass) renders the action from where it happened.
        // When zoomed in on a single tile, sounds at THAT tile play full
        // and present; sounds elsewhere fade and muffle accordingly.
        case 'harvested': {
          const pos = this.tileToWorld(e.col, e.row);
          const ct = (e as any).cropType as string | undefined;
          const id = ct === 'wheat' ? 'basket' : (ct ?? 'basket');
          if (!this.playPop(id, 'crop-cut', pos)) this.swish(1100, 0.08, 0.06);
          break;
        }
        case 'planted': {
          const pos = this.tileToWorld(e.col, e.row);
          if (this.bank?.has('hoe-till/impact')) {
            this.bank.play('hoe-till/impact', { volume: 0.35, pitch: 1.2, position: pos });
          } else {
            this.cue('seed-plant', () => this.softTap(520, 0.06, 0.05));
          }
          break;
        }
        case 'fieldPlaced': {
          // Tile fully tilled — layered hoe-till cue (thud + scrape).
          const pos = this.tileToWorld(e.col, e.row);
          if ((this.bank?.playCue('hoe-till', { position: pos }) ?? 0) === 0) {
            this.cue('hoe-till', () => this.earthyThud(160, 0.10, 0.10));
          }
          break;
        }
        case 'fieldDried': {
          const pos = this.tileToWorld(e.col, e.row);
          if (this.bank?.has('hoe-till/scrape')) {
            this.bank.play('hoe-till/scrape', { volume: 0.4, position: pos });
          } else {
            this.cue('field-dry', () => this.earthScrape(420, 0.16, 0.04));
          }
          break;
        }
        case 'strikeImpact': {
          // Per-swing impact for multi-strike chops. Tree → short
          // chop-wood cue (impact + crack). Rock → just the pickaxe
          // ring (rock-break/impact group) without the debris layer,
          // which is reserved for the final break.
          const pos = this.tileToWorld(e.col, e.row);
          if (e.kind === 'rock') {
            if (!this.bank?.play('rock-break/impact', { volume: 0.85, position: pos })) {
              this.earthyThud(220, 0.08, 0.10);
            }
          } else {
            if ((this.bank?.playCue('chop-wood', { position: pos }) ?? 0) === 0) {
              this.earthyThud(170, 0.10, 0.12);
            }
          }
          break;
        }
        case 'chopped': {
          // Three flavours, distinguished by payload:
          //   - stone yield → boulder break (rock-break)
          //   - wood ≥ 3 + acorns → full oak felled, plays the 7s
          //     chop-wood/fell clip alongside the visible tree-fall
          //     animation Diorama spawns at the same instant
          //   - everything else (fallen log chopped, stump cleared,
          //     bush / sapling cleared) → short chop-wood (impact +
          //     crack), no long falling layer
          const pos = this.tileToWorld(e.col, e.row);
          let cueName: string;
          if (e.stone != null) cueName = 'rock-break';
          else if ((e.wood ?? 0) >= 3 && e.acorns != null) cueName = 'chop-wood-fell';
          else cueName = 'chop-wood';
          if ((this.bank?.playCue(cueName, { position: pos }) ?? 0) === 0) {
            this.cue('axe-chop', () => this.earthyThud(170, 0.10, 0.12));
          }
          break;
        }
        case 'watered':
          // No file group for water yet — falls through to the trickle
          // synth. Will become positional once a water cue is registered.
          this.cue('water-can', () => this.trickle(2400, 0.10, 0.06));
          break;
        case 'sold':                 this.cue('coin-sell',       () => this.coinShing());                     break;
        case 'millStarted':          this.cue('mill-start',      () => this.earthyThud(120, 0.16, 0.14));     break;
        case 'millFinished':         this.cue('mill-finish',     () => this.millDing());                      break;
        case 'buildingPlaced': {
          const pos = this.tileToWorld(e.col, e.row);
          if ((this.bank?.playCue('building-place', { position: pos }) ?? 0) === 0) {
            this.cue('building-place', () => this.earthyThud(75, 0.30, 0.22));
          }
          break;
        }
        case 'constructionAdvanced': {
          const pos = this.tileToWorld(e.col, e.row);
          if ((this.bank?.playCue('building-raising', { position: pos }) ?? 0) === 0) {
            this.cue('stage-advance', () => this.stageAdvance());
          }
          break;
        }
        case 'constructionComplete': {
          const pos = this.tileToWorld(e.col, e.row);
          if ((this.bank?.playCue('building-constructed', { position: pos }) ?? 0) === 0) {
            this.cue('build-complete', () => this.buildFanfare());
          }
          break;
        }
        case 'materialsDeposited': {
          // Stone deposits route through the heavy-stone cue if loaded;
          // everything else uses the per-kind synth. Both get positioned.
          const pos = this.tileToWorld(e.col, e.row);
          if (e.kind === 'stone' && this.bank?.hasCue('stone-deposit')) {
            this.bank.playCue('stone-deposit', { position: pos });
          } else {
            this.playMaterialDeposit(e.kind);
          }
          break;
        }
        case 'truckArrived':  this.fireTruckCue('truck-arrive', () => this.truckHorn());                   break;
        case 'truckLoaded':   this.fireTruckCue('truck-load',   () => this.earthyThud(140, 0.08, 0.09));   break;
        case 'truckDeparted': {
          // Start the continuous motor emitter — keeps a looping source
          // playing while truck status is 'departed' or 'returning',
          // panned + attenuated by the truck's live world position.
          // posFn returns null while parked / unloading so the emitter
          // fades silently.
          this.emitters?.start('truck-motor', 'truck/motor', () => {
            const s = this.listener?.getTruckState();
            return s && s.isMoving ? { x: s.x, z: s.z } : null;
          }, 0.45);
          this.fireTruckCue('truck-depart', () => this.earthyThud(85, 0.30, 0.20));
          break;
        }
        case 'truckReturned': {
          this.emitters?.stop('truck-motor');
          this.fireTruckCue('truck-return', () => this.coinShing());
          break;
        }
        case 'platformChanged':      this.cue('crate-shuffle',   () => this.softTap(380, 0.05, 0.04));        break;
        case 'propMoved':            this.cue('prop-place',      () => this.earthyThud(140, 0.10, 0.10));     break;
        case 'plotSelectChanged':    this.cueThrottled('ui-marquee', 80, () => this.softTap(640, 0.04, 0.03));break;
        case 'plotEntered':          this.cue('plot-enter',      () => this.plotEnter());                     break;
        case 'plotExited':           this.cue('plot-exit',       () => this.plotExit());                      break;
        // Plot-mode drag events — throttled. World-positioned so a
        // long drag across the plot pans across the stereo field as
        // the cursor sweeps.
        case 'weedPlucked': {
          const now = performance.now();
          const last = this.lastPlayAt.get('weed-pluck') ?? -Infinity;
          if (now - last >= 90) {
            this.lastPlayAt.set('weed-pluck', now);
            const pos = this.tileToWorld(e.col, e.row);
            if (this.bank?.has('crop-cut/swish')) {
              this.bank.play('crop-cut/swish', { volume: 0.25, pitch: 1.2, position: pos });
            } else {
              this.earthScrape(800, 0.05, 0.06);
            }
          }
          break;
        }
        case 'subCellTilled': {
          // Per-sub-cell hoe tap during a drag stroke. Throttled 120ms
          // so a sweep doesn't fire 10 thuds in a single second.
          const now = performance.now();
          const last = this.lastPlayAt.get('hoe-tap') ?? -Infinity;
          if (now - last >= 120) {
            this.lastPlayAt.set('hoe-tap', now);
            const pos = this.tileToWorld(e.col, e.row);
            if ((this.bank?.playCue('hoe-tap', { position: pos }) ?? 0) === 0) {
              this.earthScrape(650, 0.06, 0.06);
            }
          }
          break;
        }
        case 'subCellSeeded': {
          const now = performance.now();
          const last = this.lastPlayAt.get('seed-drop') ?? -Infinity;
          if (now - last >= 120) {
            this.lastPlayAt.set('seed-drop', now);
            this.cue('seed-drop', () => this.softTap(820, 0.04, 0.04));
          }
          break;
        }
        case 'subCellWatered':       this.cueThrottled('water-sprinkle',120, () => this.trickle(3200, 0.06, 0.05));    break;
        case 'subCellHarvested': {
          // Sub-cell sickle cuts during a drag — same crop-cut cue,
          // throttled so a sweeping stroke doesn't fire 20 pops in 200ms.
          const now = performance.now();
          const last = this.lastPlayAt.get('crop-cut') ?? -Infinity;
          if (now - last >= 80) {
            this.lastPlayAt.set('crop-cut', now);
            const pos = this.tileToWorld(e.col, e.row);
            if (!this.playPop('basket', 'crop-cut', pos)) this.swish(1200, 0.05, 0.05);
          }
          break;
        }
        case 'orderPlaced':          this.cue('mailbox-place',   () => this.softTap(500, 0.06, 0.05));         break;
        case 'orderDelivered':
          if ((this.bank?.playCue('ui-chime') ?? 0) === 0) this.cue('mail-arrive', () => this.mailDing());
          break;
        case 'leveledUp':
          if ((this.bank?.playCue('ui-level-up') ?? 0) === 0) this.cue('level-up', () => this.levelFanfare());
          break;
        case 'hint': {
          // Throttled because hint events can flood from drag-paint UI feedback.
          const now = performance.now();
          const last = this.lastPlayAt.get('ui-hint') ?? -Infinity;
          if (now - last >= 140) {
            this.lastPlayAt.set('ui-hint', now);
            if ((this.bank?.playCue('ui-hint') ?? 0) === 0) this.softTap(720, 0.05, 0.03);
          }
          break;
        }
        case 'blocked': {
          const now = performance.now();
          const last = this.lastPlayAt.get('ui-deny') ?? -Infinity;
          if (now - last >= 220) {
            this.lastPlayAt.set('ui-deny', now);
            if ((this.bank?.playCue('ui-deny') ?? 0) === 0) this.softDeny();
          }
          break;
        }
        case 'zoomEntered':          this.cue('camera-zoom-in',  () => this.zoomSweep(true));                  break;
        case 'zoomExited':           this.cue('camera-zoom-out', () => this.zoomSweep(false));                 break;
        case 'inspect':              this.cueThrottled('ui-inspect',150, () => this.softTap(720, 0.04, 0.04)); break;
        case 'itemLanded':           this.playItemLand(e.id); break;
        // 'stateChanged' is intentionally silent — too frequent.
      }
    });
  }

  /** External listener-state hookup. Call once from main.ts after both
   *  Diorama and Sounds are constructed. Sounds then drives the
   *  listener position + zoom each frame inside its rAF loop, and
   *  uses the truck-state accessor to position the motor emitter. */
  connectListener(provider: ListenerProvider): void {
    this.listener = provider;
  }

  /** Hook up the Settings store. Subscribes to settings changes and
   *  applies them to the audio bus — master volume + mute. Safe to
   *  call before the AudioContext has unlocked; the bank-init path
   *  re-applies the volume once the bank exists. */
  connectSettings(settings: Settings): void {
    if (this.settingsUnsub) this.settingsUnsub();
    this.settings = settings;
    this.settingsUnsub = settings.subscribe(() => this.applySettingsVolume());
    this.applySettingsVolume();
  }

  /** Push the settings store's effective bus gain to the bank. No-op
   *  if either side isn't ready yet. Folds mute + slider position
   *  into one value via Settings.effectiveBusGain() — there's no
   *  separate "muted" state inside SoundBank, just the resulting gain. */
  private applySettingsVolume(): void {
    if (!this.bank || !this.settings) return;
    this.bank.setMasterVolume(this.settings.effectiveBusGain());
  }

  /** Fire a truck-anchored cue at the truck's current world position.
   *  Falls back to the synth callback if the bank cue isn't loaded or
   *  the truck hasn't been spawned yet. */
  private fireTruckCue(cueName: string, fallback: () => void): void {
    const s = this.listener?.getTruckState();
    const position = s ? { x: s.x, z: s.z } : undefined;
    if ((this.bank?.playCue(cueName, { position }) ?? 0) === 0) {
      fallback();
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────

  /** Begin lazy-loading the given sample names from `/sounds/{name}.mp3`.
   *  Safe to call many times; already-loaded / known-failed names are skipped. */
  loadSamples(names: string[]): void {
    for (const name of names) {
      if (this.samples.has(name) || this.failed.has(name)) continue;
      const a = new Audio(`/sounds/${name}.mp3`);
      a.preload = 'auto';
      // Track failure so we don't keep re-attempting on every play().
      a.addEventListener('error', () => {
        this.failed.add(name);
        this.samples.delete(name);
      }, { once: true });
      this.samples.set(name, a);
    }
  }

  /** Play a named cue. Returns true if a sample was triggered, false otherwise
   *  (caller can decide to run a fallback — `cue()` does this internally). */
  play(name: string, volume = 1): boolean {
    const master = this.samples.get(name);
    if (!master || this.failed.has(name)) return false;
    // Clone so overlapping fires (e.g. rapid weed-pluck) don't cut each other.
    const inst = master.cloneNode(true) as HTMLAudioElement;
    inst.volume = Math.max(0, Math.min(1, volume));
    const p = inst.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay block — ignore */ });
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Internals
  // ─────────────────────────────────────────────────────────────────────

  /** Try the named sample first; fall back to the synth callback if missing. */
  private cue(name: string, fallback: () => void): void {
    if (!this.play(name)) fallback();
  }

  /** Same as `cue` but skips fires that happen within `minGapMs` of the
   *  previous fire for the same name. Used for drag-paint events that
   *  would otherwise spam the audio bus. */
  private cueThrottled(name: string, minGapMs: number, fallback: () => void): void {
    const now = performance.now();
    const last = this.lastPlayAt.get(name) ?? -Infinity;
    if (now - last < minGapMs) return;
    this.lastPlayAt.set(name, now);
    this.cue(name, fallback);
  }

  /** Per-material deposit cue. Each construction material gets a
   *  distinctly-shaped synth so the player gets pleasing variety as
   *  they fill the on-site crate — wood-thud, stone-clack, sack-thump,
   *  coin-clink. Throttled at 60ms per kind so a single multi-icon
   *  deposit (qty>1) doesn't stack into a buzzy chord. */
  private playMaterialDeposit(kind: 'money' | 'wood' | 'wheat' | 'stone'): void {
    const name = `material-${kind}`;
    const now = performance.now();
    const last = this.lastPlayAt.get(name) ?? -Infinity;
    if (now - last < 60) return;
    this.lastPlayAt.set(name, now);
    // Route stone deposits through the SoundBank cue if loaded.
    if (kind === 'stone' && (this.bank?.playCue('stone-deposit') ?? 0) > 0) {
      return;
    }
    this.cue(name, () => {
      switch (kind) {
        case 'wood':  this.woodThud();    break;
        case 'stone': this.stoneClack();  break;
        case 'wheat': this.sackThump();   break;
        case 'money': this.coinClink();   break;
      }
    });
  }

  /** Per-item pop when a fly-to-HUD glyph lands on its chip. Routes
   *  through the SoundBank pop-land variation pool — same family the
   *  harvest event uses, so a "cut → fly → land" sequence reads as a
   *  cohesive arc rather than three unrelated noises.
   *
   *  Hard 15ms floor, rolling burst counter, per-resource pitch + small
   *  jitter all preserved. Synth fallback fires when pop-land isn't
   *  loaded (so the game stays audible during dev). */
  private playItemLand(id: string): void {
    const now = performance.now();
    const key = `land:${id}`;
    const last = this.lastPlayAt.get(key) ?? -Infinity;
    if (now - last < 15) return;
    this.lastPlayAt.set(key, now);
    if (!this.playPop(id)) {
      // Fallback — synth balloon pop with same per-resource pitch math
      // the bank-driven path uses (so the dev experience matches even
      // when no real clips are loaded yet).
      const k = this.popClimb(id);
      const synthBase: Record<string, [number, number]> = {
        money:      [2200, 0.22], basket: [1600, 0.20], corn: [1300, 0.20],
        carrot:     [1100, 0.20], flour:  [1800, 0.18], cornFlour: [1700, 0.18],
        wood:       [ 600, 0.22], stone:  [ 450, 0.24], mushroom:  [ 900, 0.22],
        berry:      [1500, 0.20], treeSeeds: [750, 0.20],
      };
      const [base, peak] = synthBase[id] ?? [1200, 0.18];
      this.balloonPop(base * k, peak);
      if (id === 'money') setTimeout(() => this.balloonPop(2800 * k, 0.16), 45);
    }
  }

  /** Bank-driven pop with per-resource pitch + rolling-burst climb +
   *  jitter. Routes through the named `chip-land` cue (HUD landing) or
   *  `crop-cut` cue (sickle harvest) so the composition lives in one
   *  editable spot. Returns false when the bank has no pop-land loaded
   *  so the caller can fire a synth fallback.
   *
   *  When `pos` is set the cue is positioned in the world — used for
   *  harvest pops at the tile being cut so the sickle sound emits
   *  from the right spot. HUD chip-land pops (`playItemLand`) call
   *  without `pos` since the chip is a fixed UI element. */
  private playPop(id: string, cue: 'chip-land' | 'crop-cut' = 'chip-land',
                  pos?: { x: number; z: number }): boolean {
    if (!this.bank || !this.bank.has('chip-land/pop')) return false;
    const k = this.popClimb(id);
    const basePitch = POP_PITCH[id] ?? 1.0;
    const fired = this.bank.playCue(cue, { pitchMul: basePitch * k, position: pos });
    return fired > 0;
  }

  /** Rolling burst-climb factor for a given resource id. First pop in a
   *  burst = 1.0, each subsequent pop within 500ms adds ~7% pitch up to
   *  +25%, then resets after silence. ±5% jitter on top so identical
   *  items don't sound robotic. */
  private popClimb(id: string): number {
    const now = performance.now();
    const burstKey = `land-burst:${id}`;
    const burstStartKey = `land-burst-t:${id}`;
    const burstStart = this.lastPlayAt.get(burstStartKey) ?? -Infinity;
    let burst = (this.lastPlayAt.get(burstKey) ?? 0);
    if (now - burstStart > 500) burst = 0;
    burst++;
    this.lastPlayAt.set(burstKey, burst);
    this.lastPlayAt.set(burstStartKey, now);
    const climb = 1 + Math.min(0.25, 0.07 * (burst - 1));
    const jitter = 0.95 + Math.random() * 0.10;
    return climb * jitter;
  }

  /** Register the static SOUND_GROUPS map with the bank. Fire-and-forget;
   *  groups load in parallel and become available as soon as each one's
   *  files decode. Anything not yet loaded silently falls through to
   *  synth via the per-call `has()` checks.
   *
   *  After registration, cues are defined that compose the groups into
   *  named gameplay sounds. Game events call `playCue(name)` and the
   *  composition lives entirely here. */
  private registerSoundGroups() {
    if (!this.bank) return;
    for (const [group, cfg] of Object.entries(SOUND_GROUPS)) {
      this.bank.loadGroup(group, cfg).then((r) => {
        if (r.failed > 0) {
          // eslint-disable-next-line no-console
          console.warn(`[Sounds] ${r.group}: loaded ${r.loaded}, failed ${r.failed}`);
        }
      });
    }
    this.defineCues();
  }

  /** Register every named composite cue. Each cue is the FULL sound
   *  design for one gameplay event — edit them here and every call site
   *  in the game updates at once.
   *
   *  Layering convention: attack (1.0) + body (0.7-0.9) + tail (0.4-0.6)
   *  + detail (0.3-0.5). Volumes anywhere outside this range usually
   *  signal that the mix needs rebalancing rather than the layer needs
   *  to be louder.
   */
  private defineCues() {
    if (!this.bank) return;

    // HUD chip-land pop. Single layer; per-call pitchMul carries the
    // burst-climb + per-resource pitch from playPop().
    this.bank.defineCue('chip-land', [
      { group: 'chip-land/pop' },
    ]);

    // Crop cut — sickle harvest. Three-layer composition: blade swish
    // through air → blade contact + plant fibers tearing → satisfaction
    // pop on collect. Each layer falls back gracefully if its group
    // isn't loaded yet.
    this.bank.defineCue('crop-cut', [
      { group: 'crop-cut/swish',  volume: 0.6 },
      { group: 'crop-cut/snip',   volume: 0.4, delayMs: 30 },
      { group: 'chip-land/pop',   volume: 0.7, delayMs: 60, pitch: 0.95 },
    ]);

    // Hoe tile complete (fieldPlaced). Attack + body composition:
    // percussive thud-front, then a softer dirt-scrape texture 40ms
    // later for the "strike + part" character.
    this.bank.defineCue('hoe-till', [
      { group: 'hoe-till/impact', volume: 0.85 },
      { group: 'hoe-till/scrape', volume: 0.55, delayMs: 40 },
    ]);

    // Hoe sub-cell tap (subCellTilled, fires often, capped to 150ms
    // playback by the group's maxLengthMs). Single layer only — any
    // second layer turns a drag stroke into mush.
    this.bank.defineCue('hoe-tap', [
      { group: 'hoe-till/impact', volume: 0.65 },
    ]);

    // Rock break — big thud + gravel scatter 60ms later. Reads as
    // "the rock cracked and bits fell off." Heavy-stone variant layered
    // in 30% strength behind the pickaxe ring for body.
    this.bank.defineCue('rock-break', [
      { group: 'rock-break/impact', volume: 0.9 },
      { group: 'stone/heavy',       volume: 0.3, delayMs: 20 },
      { group: 'rock-break/debris', volume: 0.5, delayMs: 60 },
    ]);

    // Stone deposited into the construction crate. Heavier rock-impact
    // pool with no debris — single block placement.
    this.bank.defineCue('stone-deposit', [
      { group: 'stone/heavy', volume: 0.85 },
    ]);

    // ── chop-wood — attack thud + splinter detail. Per-axe-strike;
    // the bigger 'chop-wood-fell' cue is for the moment the tree
    // actually drops.
    this.bank.defineCue('chop-wood', [
      { group: 'chop-wood/impact', volume: 0.95 },
      { group: 'chop-wood/crack',  volume: 0.45, delayMs: 60 },
    ]);
    this.bank.defineCue('chop-wood-fell', [
      { group: 'chop-wood/impact', volume: 0.9 },
      { group: 'chop-wood/fell',   volume: 0.7, delayMs: 80 },
      { group: 'chop-wood/crack',  volume: 0.4, delayMs: 30 },
    ]);

    // ── building cues. Place = thunk on drop. Raising = under-
    // construction pulse (single layer, the source file has the
    // hammer cadence baked in). Constructed = victory chime.
    this.bank.defineCue('building-place', [
      { group: 'building/place',  volume: 0.85 },
      { group: 'building/rubble', volume: 0.25, delayMs: 30, pitch: 0.9 },
    ]);
    this.bank.defineCue('building-raising', [
      { group: 'building/raising', volume: 0.7 },
    ]);
    this.bank.defineCue('building-constructed', [
      { group: 'building/constructed', volume: 0.8 },
    ]);

    // ── truck cues. arrive = motor body + tires under brake hiss +
    // double-horn announcement. depart = horn first, then motor +
    // tires accelerating. load = thump into the cargo bed.
    this.bank.defineCue('truck-arrive', [
      { group: 'truck/motor',        volume: 0.45 },
      { group: 'truck/tires',        volume: 0.30, delayMs: 30 },
      { group: 'truck/brake',        volume: 0.45, delayMs: 280 },
      { group: 'truck/horn',         volume: 0.65, delayMs: 480 },
      { group: 'truck/engine-stop',  volume: 0.45, delayMs: 700, pitch: 0.92 },
    ]);
    this.bank.defineCue('truck-depart', [
      { group: 'truck/horn',  volume: 0.55 },
      { group: 'truck/motor', volume: 0.50, delayMs: 220, pitch: 1.08 },
      { group: 'truck/tires', volume: 0.35, delayMs: 260 },
    ]);
    this.bank.defineCue('truck-load', [
      { group: 'truck/load', volume: 0.85 },
    ]);
    /** Truck has finished the round trip and parked back at the depot.
     *  Uses the engine-stop file (a wind-down) layered over the chime
     *  from ui/level-up — a satisfied "deal closed" feel. */
    this.bank.defineCue('truck-return', [
      { group: 'truck/engine-stop', volume: 0.55 },
      { group: 'ui/chime',          volume: 0.6, delayMs: 250 },
    ]);

    // ── UI cues — single-layer composition for each.
    this.bank.defineCue('ui-deny',     [{ group: 'ui/deny',     volume: 0.6 }]);
    this.bank.defineCue('ui-hint',     [{ group: 'ui/hint',     volume: 0.5 }]);
    this.bank.defineCue('ui-chime',    [{ group: 'ui/chime',    volume: 0.7 }]);
    this.bank.defineCue('ui-level-up', [{ group: 'ui/level-up', volume: 0.8 }]);
  }

  // ─────────────────────────────────────────────────────────────────────
  // World-space helpers — convert sim tile coords to audio world coords.
  // Origin is the plot center; tile (0,0) sits at NW corner of the plot
  // so x = col - PLOT_W/2 + 0.5, z = row - PLOT_H/2 + 0.5 puts the
  // listener at world origin when the camera is centred on the plot.
  // Matches Diorama.tileCenter() so emitters and visuals stay aligned.
  // ─────────────────────────────────────────────────────────────────────

  /** Tile (col, row) → world (x, z). y is always 0 (ground plane).
   *  Use to enrich game-event playCue calls with a `position` so the
   *  cue emits from the actual tile rather than monaurally at the
   *  listener's location. */
  private tileToWorld(col: number, row: number): { x: number; z: number } {
    return { x: col - PLOT_W / 2 + 0.5, z: row - PLOT_H / 2 + 0.5 };
  }

  // ─────────────────────────────────────────────────────────────────────
  // Public sound-design API — for code that wants to fire bank groups
  // directly (layered cues, ambient one-shots, etc).
  // ─────────────────────────────────────────────────────────────────────

  /** Play one variation of a registered group. Returns false if the
   *  group isn't loaded — caller can run a fallback. */
  playGroup(group: string, opts: PlayOpts = {}): boolean {
    return this.bank?.play(group, opts) ?? false;
  }

  /** Play a layered "mix" — N groups fired together with optional
   *  delays + per-layer pitch/volume. Useful for composite cues like
   *  "harvest = swish + pop + sparkle" where you want the layers to
   *  share timing semantics. */
  playMix(layers: MixLayer[]): number {
    return this.bank?.playMix(layers) ?? 0;
  }

  /** Fire a registered cue by name. `opts.pitchMul` / `volumeMul` scale
   *  every layer of the cue; `delayMs` shifts the whole composition
   *  later. Returns the count of layers that actually fired (0 if the
   *  cue isn't registered or none of its groups loaded). */
  playCue(name: string, opts: CueOpts = {}): number {
    return this.bank?.playCue(name, opts) ?? 0;
  }

  /** Drive the AmbientMixer per frame via requestAnimationFrame:
   *  - Set each bed's target volume based on game state (time of day,
   *    plot mode, eventually weather).
   *  - Tick the mixer so it smooths volumes + handles crossfades.
   *
   *  Self-schedules. Stops if the page is hidden (rAF naturally pauses
   *  on background tabs) and resumes when visible. */
  private startAmbientLoop() {
    if (this.ambientLoopStarted) return;
    this.ambientLoopStarted = true;
    this.ambientLastTs = performance.now();
    const tick = (ts: number) => {
      const dt = Math.min(0.1, (ts - this.ambientLastTs) / 1000);   // clamp to 100ms
      this.ambientLastTs = ts;
      // Pull the camera's current listener state every frame so
      // positional plays + the truck motor emitter render from the
      // right point of view. Listener is only set if the Diorama has
      // been connected via connectListener().
      if (this.listener && this.bank) {
        const s = this.listener.getListenerState();
        this.bank.setListener(s.x, s.z, s.zoom);
      }
      if (this.ambient) {
        this.driveAmbientState();
        this.ambient.update(dt);
      }
      // Tick positional emitters (truck motor while driving etc.) —
      // each emitter re-reads its world position and smooths its
      // pan / gain / lowpass toward the new spatial factors.
      if (this.emitters) this.emitters.update(dt);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /** Map sim state → per-bed target volumes. Called every frame. The
   *  mixer smoothly ramps toward these values, so abrupt state changes
   *  (entering plot mode, dusk falling) read as natural fades rather
   *  than pops.
   *
   *  Beds currently handled:
   *  - `forest`      — main daytime woodland bed, loud during day,
   *                    softer at night
   *  - `crickets`    — night-only insect chorus
   *  - `wind`        — quiet always (placeholder for weather state)
   *  - `rain`        — only during rain weather (placeholder)
   *
   *  Plot-mode duck: every bed drops to 40% in plot mode so player
   *  focus shifts to plot tile sounds. */
  private driveAmbientState() {
    if (!this.ambient || !this.bank) return;
    const tod = this.sim.getTimeOfDay();
    // 0 at midnight, 1 at noon, 0 at midnight — symmetric day curve.
    const dayCurve = Math.max(0, Math.sin((tod - 0.25) * Math.PI * 2));
    const nightCurve = Math.max(0, -Math.sin((tod - 0.25) * Math.PI * 2));
    const plotDuck = this.sim.state.plotInView ? 0.4 : 1.0;

    // Forest day-bed — loudest at noon, faded at dawn/dusk, silent at
    // 02:00 (deepest night).
    const forestVol = 0.30 * dayCurve * plotDuck;
    if (this.bank.has('environment/forest')) {
      this.ambient.setBed('forest', 'environment/forest', forestVol);
    }

    // Crickets — opposite curve, loudest in deep night.
    const cricketsVol = 0.25 * nightCurve * plotDuck;
    if (this.bank.has('environment/crickets')) {
      this.ambient.setBed('crickets', 'environment/crickets', cricketsVol);
    }

    // Wind — quiet always (will get more sophisticated with weather state).
    const windVol = 0.15 * plotDuck;
    if (this.bank.has('environment/wind')) {
      this.ambient.setBed('wind', 'environment/wind', windVol);
    }

    // Rain — placeholder, off until a weather state field exists in sim.
    // const rainVol = isRaining ? 0.30 * plotDuck : 0;
    // if (this.bank.has('environment/rain')) {
    //   this.ambient.setBed('rain', 'environment/rain', rainVol);
    // }
  }

  /** Minimal ambient mixer for the `environment/birds` group — fires a
   *  random bird call every 8-25 seconds during daytime hours.
   *  Each call lands at a random stereo pan so the forest feels alive.
   *  Skips silently if the bank doesn't have the group loaded yet
   *  (e.g. environment/birds folder is empty).
   *
   *  This is the placeholder until a full ambient mixer arrives (forest
   *  bed loop + wind + weather state). For now it's just birds. */
  private startBirdScheduler() {
    if (this.birdSchedulerStarted) return;
    this.birdSchedulerStarted = true;
    const tick = () => {
      if (!this.bank) {
        setTimeout(tick, 5000);
        return;
      }
      if (this.bank.has('environment/birds')) {
        const tod = this.sim.getTimeOfDay();
        // Daytime = 06:00 to 19:00 — i.e. tod between 0.25 and 0.79.
        const isDay = tod > 0.25 && tod < 0.79;
        // Roughly 60% chance of firing per tick during daytime so it
        // feels alive but not constant.
        if (isDay && Math.random() < 0.6) {
          const pan = (Math.random() * 2 - 1) * 0.6;   // ±0.6 stereo spread
          this.bank.play('environment/birds', { pan });
        }
      }
      // 8-25 seconds until next attempt.
      setTimeout(tick, 8000 + Math.random() * 17000);
    };
    setTimeout(tick, 5000);
  }

  /** Bubble-wrap "POP" — the satisfying click that ADHD-bait videos are
   *  built on. Two layered components fired at the same instant:
   *
   *    1. CLICK — a 3ms broadband-noise transient, high-pass filtered so
   *       it reads as a "snap" / fingernail tick. Provides the percussive
   *       front edge.
   *    2. PLOK — a fast downward pitch sweep on a sine osc (1.6× → 0.4×
   *       of `centerFreq`) over ~28ms. The downward glide is what makes
   *       the ear hear "pop!" instead of "boop". The sine gives the pop
   *       a tonal body without sounding melodic.
   *
   *  `centerFreq` tunes the "bubble size": low = big bubble (wood, stone),
   *  high = small bubble (money, flour). */
  private balloonPop(centerFreq: number, peak = 0.20) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t0 = ctx.currentTime;

    // ── 1. CLICK — 3ms high-passed noise transient ──────────────
    const clickDur = 0.003;
    const clickLen = Math.max(1, Math.floor(ctx.sampleRate * clickDur));
    const clickBuf = ctx.createBuffer(1, clickLen, ctx.sampleRate);
    const cd = clickBuf.getChannelData(0);
    for (let i = 0; i < clickLen; i++) {
      // Decaying noise — front of the buffer is the loudest.
      cd[i] = (Math.random() * 2 - 1) * (1 - i / clickLen);
    }
    const clickSrc = ctx.createBufferSource();
    clickSrc.buffer = clickBuf;
    const clickHpf = ctx.createBiquadFilter();
    clickHpf.type = 'highpass';
    // High-pass at half the bubble's center frequency — higher pops have
    // a crisper click, lower pops a duller thud-click.
    clickHpf.frequency.setValueAtTime(Math.max(800, centerFreq * 0.6), t0);
    const clickG = ctx.createGain();
    clickG.gain.setValueAtTime(0.0001, t0);
    clickG.gain.exponentialRampToValueAtTime(peak * 1.2, t0 + 0.0005);
    clickG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.008);
    clickSrc.connect(clickHpf).connect(clickG).connect(ctx.destination);
    clickSrc.start(t0);
    clickSrc.stop(t0 + 0.015);

    // ── 2. PLOK — sine sweep dropping ~2 octaves over 28ms ──────
    const plok = ctx.createOscillator();
    plok.type = 'sine';
    plok.frequency.setValueAtTime(centerFreq * 1.6, t0);
    plok.frequency.exponentialRampToValueAtTime(centerFreq * 0.4, t0 + 0.028);
    const plokG = ctx.createGain();
    plokG.gain.setValueAtTime(0.0001, t0);
    plokG.gain.exponentialRampToValueAtTime(peak * 0.95, t0 + 0.002);
    plokG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.040);
    plok.connect(plokG).connect(ctx.destination);
    plok.start(t0);
    plok.stop(t0 + 0.060);
  }

  private now() { return this.ctx?.currentTime ?? 0; }

  private envelope(node: GainNode, attack: number, release: number, peak = 0.18) {
    if (!this.ctx) return;
    const t = this.now();
    node.gain.cancelScheduledValues(t);
    node.gain.setValueAtTime(0.0001, t);
    node.gain.exponentialRampToValueAtTime(peak, t + attack);
    node.gain.exponentialRampToValueAtTime(0.0001, t + attack + release);
  }

  private osc(freq: number, type: OscillatorType, dur: number, peak = 0.18) {
    if (!this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, this.now());
    o.connect(g).connect(this.ctx.destination);
    this.envelope(g, 0.005, dur, peak);
    o.start();
    o.stop(this.now() + dur + 0.05);
  }

  private pop(freq: number, dur: number, peak: number) {
    if (!this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'triangle';
    const t = this.now();
    o.frequency.setValueAtTime(freq * 1.6, t);
    o.frequency.exponentialRampToValueAtTime(freq, t + dur * 0.7);
    o.connect(g).connect(this.ctx.destination);
    this.envelope(g, 0.003, dur, peak);
    o.start();
    o.stop(t + dur + 0.05);
  }

  private thud(freq: number, dur: number) {
    if (!this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'sine';
    const t = this.now();
    o.frequency.setValueAtTime(freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.4, t + dur);
    o.connect(g).connect(this.ctx.destination);
    this.envelope(g, 0.004, dur, 0.22);
    o.start();
    o.stop(t + dur + 0.05);
  }

  private coin() {
    this.osc(1320, 'square', 0.06, 0.10);
    setTimeout(() => this.osc(1760, 'square', 0.08, 0.10), 60);
  }

  private chime(big = false) {
    const base = big ? 660 : 880;
    this.osc(base,        'triangle', 0.18, 0.14);
    setTimeout(() => this.osc(base * 1.5,  'triangle', 0.18, 0.12), 70);
    setTimeout(() => this.osc(base * 2.0,  'triangle', 0.22, 0.10), 140);
  }

  /**
   * Soft "nuh-uh" for a blocked action. Two short triangle-wave notes
   * descending a minor third — gentle, voice-like, easy to ignore on
   * the 10th misclick of a session but still clear enough to register
   * "that didn't work" on the first try.
   *
   * Triangle wave (not sawtooth) keeps the timbre warm; the low peak
   * gain + slow attack (~10ms) keeps it from biting.
   */
  private softDeny() {
    if (!this.ctx) return;
    this.osc(440, 'triangle', 0.07, 0.05);
    setTimeout(() => this.osc(370, 'triangle', 0.10, 0.04), 55);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Naturalistic noise synths — used for events that should feel like
  // physical material (earth, water, plant fiber) instead of a beep.
  // ─────────────────────────────────────────────────────────────────────

  /** Lowpass-filtered noise — earthy / scratchy / dull. Hoe scrape, weed
   *  pluck, soft impact. `centerFreq` ≈ the LPF cutoff (in Hz). */
  private earthScrape(centerFreq: number, dur: number, peak: number) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const taper = i < len * 0.05 ? 1 : Math.max(0, 1 - (i - len * 0.05) / (len * 0.95));
      data[i] = (Math.random() * 2 - 1) * taper;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lpf = ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.setValueAtTime(centerFreq, t);
    lpf.Q.setValueAtTime(0.7, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(lpf).connect(g).connect(ctx.destination);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  /** Highpass-filtered noise — water sprinkle, paper rustle, hiss. */
  private trickle(hpFreq: number, dur: number, peak: number) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.max(0, 1 - i / len);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hpf = ctx.createBiquadFilter();
    hpf.type = 'highpass';
    hpf.frequency.setValueAtTime(hpFreq, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(hpf).connect(g).connect(ctx.destination);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  /** Sickle "swish" — bandpass noise with a downward pitch sweep. */
  private swish(centerFreq: number, dur: number, peak: number) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(centerFreq * 1.6, t);
    bp.frequency.exponentialRampToValueAtTime(centerFreq * 0.5, t + dur);
    bp.Q.setValueAtTime(3.0, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  /** Earthy thud — sine body with a quick pitch drop + a brief lowpass-
   *  noise transient. Reads as impact-on-earth instead of a sub bass note. */
  private earthyThud(centerFreq: number, dur: number, peak: number) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(centerFreq * 1.4, t);
    o.frequency.exponentialRampToValueAtTime(centerFreq * 0.55, t + dur);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, t);
    og.gain.exponentialRampToValueAtTime(peak, t + 0.003);
    og.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(og).connect(ctx.destination);
    o.start(t);
    o.stop(t + dur + 0.04);
    // Transient — short lowpass noise click that gives the thud a "body
    // contact" attack.
    this.earthScrape(centerFreq * 4, 0.018, peak * 0.45);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Construction material deposits — ADHD-friendly: each kind has a
  // distinctly shaped synth so repeated deposits read as a pleasing
  // rhythm, not a single repeating beep. Pitch jitter per fire keeps
  // back-to-back deposits feeling alive instead of mechanical.
  // ─────────────────────────────────────────────────────────────────────

  /** Wood log dropping onto a stack — warm low-mid thud + a tight tick
   *  on top so the player hears both the bulk impact and the wood-on-
   *  wood snap. Pitch wobbles ±6% per fire. */
  private woodThud() {
    if (!this.ctx) return;
    const j = 1 + (Math.random() - 0.5) * 0.12;        // ±6% pitch jitter
    const ctx = this.ctx;
    const t = ctx.currentTime;
    // Low-mid body (≈100Hz center) dropping like a felled log.
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(150 * j, t);
    o.frequency.exponentialRampToValueAtTime(70 * j, t + 0.16);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, t);
    og.gain.exponentialRampToValueAtTime(0.20, t + 0.003);
    og.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    o.connect(og).connect(ctx.destination);
    o.start(t); o.stop(t + 0.20);
    // Tick — short high-freq scrape that lands as the wood "clack" on top
    // of the bulk thud.
    this.earthScrape(720 * j, 0.025, 0.10);
  }

  /** Stone hitting stone — sharper, brighter, less body. Two overlapping
   *  ticks at slightly different frequencies for a "clack-clack" feel. */
  private stoneClack() {
    if (!this.ctx) return;
    const j = 1 + (Math.random() - 0.5) * 0.14;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    // Primary clack — short noise burst centered high.
    this.earthScrape(1800 * j, 0.018, 0.18);
    // Secondary tick 25ms later, slightly higher freq — "double tap" feel.
    setTimeout(() => {
      if (!this.ctx) return;
      const t2 = this.ctx.currentTime;
      this.earthScrape(2400 * j, 0.014, 0.12);
      // Tiny rocky body — short triangle wave for the stone's mass.
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(380 * j, t2);
      o.frequency.exponentialRampToValueAtTime(220 * j, t2 + 0.06);
      const og = ctx.createGain();
      og.gain.setValueAtTime(0.0001, t2);
      og.gain.exponentialRampToValueAtTime(0.10, t2 + 0.002);
      og.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.06);
      o.connect(og).connect(this.ctx.destination);
      o.start(t2); o.stop(t2 + 0.08);
    }, 25);
    void t;
  }

  /** Wheat sack thumping ground — soft muffled thud + a brief grain-dust
   *  rustle. Lowest-frequency of the four materials so it feels heavy
   *  but not sharp. */
  private sackThump() {
    if (!this.ctx) return;
    const j = 1 + (Math.random() - 0.5) * 0.10;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    // Muffled body — sub-200Hz sine falling fast.
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(180 * j, t);
    o.frequency.exponentialRampToValueAtTime(60 * j, t + 0.14);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, t);
    og.gain.exponentialRampToValueAtTime(0.22, t + 0.005);
    og.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
    o.connect(og).connect(ctx.destination);
    o.start(t); o.stop(t + 0.18);
    // Grain dust — short bandpass noise to suggest jute fabric + wheat
    // settling. Quiet so it sits behind the body.
    this.earthScrape(900 * j, 0.06, 0.05);
  }

  /** Single coin landing — bright, short, and crisp. Lighter than the
   *  multi-coin coinShing() sale jingle so it reads as one piece. */
  private coinClink() {
    if (!this.ctx) return;
    const j = 1 + (Math.random() - 0.5) * 0.10;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    // Two stacked sine partials — a perfect-fifth coin "ting".
    const playTone = (freq: number, gain: number, delay = 0) => {
      const t0 = t + delay;
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(freq * j, t0);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(gain, t0 + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
      o.connect(g).connect(ctx.destination);
      o.start(t0); o.stop(t0 + 0.20);
    };
    playTone(1320, 0.10);
    playTone(1980, 0.07, 0.005);     // perfect fifth above, tiny offset
  }

  /** Stage-complete cue — layered thud + bright bell so the player gets
   *  a strong "yes! that worked" hit when a construction stage finishes.
   *  Bigger than per-material deposit, smaller than full buildFanfare. */
  private stageAdvance() {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    // Hammer-thud body — single firm impact.
    this.earthyThud(110, 0.20, 0.20);
    // Bright bell — short triangle ding ~120ms after, signaling success.
    setTimeout(() => {
      if (!this.ctx) return;
      const t2 = this.ctx.currentTime;
      const playBell = (freq: number, gain: number) => {
        const o = this.ctx!.createOscillator();
        o.type = 'triangle';
        o.frequency.setValueAtTime(freq, t2);
        const g = this.ctx!.createGain();
        g.gain.setValueAtTime(0.0001, t2);
        g.gain.exponentialRampToValueAtTime(gain, t2 + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, t2 + 0.32);
        o.connect(g).connect(this.ctx!.destination);
        o.start(t2); o.stop(t2 + 0.36);
      };
      // Major-third stack — 880 + 1100 Hz reads as "achievement".
      playBell(880,  0.16);
      playBell(1100, 0.10);
    }, 120);
    void t;
  }

  /**
   * Truck arrival cue — a short engine rumble that fades into a friendly
   * two-tone horn honk. Cartoon delivery-truck flavor (think "ba-baah!")
   * so the player recognizes "the truck is here" from the next room
   * without confusing it with the mill / mailbox / level-up chimes.
   *
   *   0–250ms : low rumble (lowpass noise, ~200Hz)
   *   ~120ms  : honk 1 — dual triangle wave (E4 + A4, a perfect fourth)
   *   ~340ms  : honk 2 — same chord, slightly punchier
   */
  private truckHorn() {
    if (!this.ctx) return;
    const ctx = this.ctx;
    // Engine rumble underneath the honk.
    this.earthScrape(220, 0.30, 0.05);
    const dualHonk = (when: number, peak: number) => {
      if (!this.ctx) return;
      const t0 = ctx.currentTime + when;
      const dur = 0.18;
      const playTone = (freq: number, gain: number) => {
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.setValueAtTime(freq, t0);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
        o.connect(g).connect(ctx.destination);
        o.start(t0);
        o.stop(t0 + dur + 0.02);
      };
      // E4 + A4 — the classic "diesel horn" interval (perfect fourth).
      playTone(330, peak);
      playTone(440, peak * 0.85);
    };
    dualHonk(0.12, 0.08);
    dualHonk(0.34, 0.07);
  }

  /** Soft UI tap — gentle triangle-wave tone. Used for hint / inspect /
   *  small-ack cues that fire often and need to stay polite. */
  private softTap(freq: number, dur: number, peak: number) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(freq, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Distinct one-shot cues — each event that used to share chime() now
  // gets its own voice so the player can tell what happened from the
  // sound alone.
  // ─────────────────────────────────────────────────────────────────────

  /** Mill finishes a batch — slow-grind down + a single bell ding.
   *  Reads as "the stones stopped turning, the flour is ready". */
  private millDing() {
    if (!this.ctx) return;
    // Engine-grind decay — low pitched lowpass noise that tapers off.
    this.earthScrape(280, 0.20, 0.05);
    // Bell ding 100ms after the grind starts settling.
    setTimeout(() => this.osc(1320, 'triangle', 0.18, 0.10), 100);
  }

  /** Mailbox order arrived — a single sharp envelope-flap + a chirp.
   *  Stays brief; reads as "letter in the box" not "fanfare". */
  private mailDing() {
    if (!this.ctx) return;
    // Paper-flap: short midpass noise pulse.
    this.trickle(2200, 0.05, 0.06);
    // Chirp 60ms later.
    setTimeout(() => this.osc(1100, 'triangle', 0.10, 0.08), 60);
  }

  /** Plot adopted + dive-cam begins — woosh-in + warm resolve chord. */
  private plotEnter() {
    if (!this.ctx) return;
    // Whoosh-in: bandpass noise sweeping UP, telling the ear we're
    // moving INTO something.
    this.upSweep(360, 1200, 0.18, 0.05);
    // Warm two-note resolve (root + fifth) after the whoosh.
    setTimeout(() => {
      this.osc(523, 'triangle', 0.18, 0.07); // C5
      this.osc(784, 'triangle', 0.18, 0.05); // G5 — same time, octave + 5th
    }, 140);
  }

  /** Plot exited — descending sigh that mirrors plotEnter's whoosh. */
  private plotExit() {
    if (!this.ctx) return;
    this.upSweep(1200, 400, 0.18, 0.05);     // sweep DOWN by passing reversed args
    setTimeout(() => this.osc(440, 'triangle', 0.14, 0.05), 100);
  }

  /** Building stage completed — three quick hammer-down thuds + a triumphant
   *  major triad. Distinct from levelFanfare by leading with percussion. */
  private buildFanfare() {
    if (!this.ctx) return;
    // Three rapid hammer thuds — done!
    this.earthyThud(160, 0.06, 0.10);
    setTimeout(() => this.earthyThud(160, 0.06, 0.10), 80);
    setTimeout(() => this.earthyThud(160, 0.06, 0.10), 160);
    // Major triad — C5 + E5 + G5 simultaneously, soft attack.
    setTimeout(() => {
      this.osc(523, 'triangle', 0.30, 0.07);
      this.osc(659, 'triangle', 0.30, 0.06);
      this.osc(784, 'triangle', 0.34, 0.05);
    }, 260);
  }

  /** Player gained a level — actual rising fanfare. Four ascending notes
   *  building to a sustained top note so it FEELS like progression. */
  private levelFanfare() {
    if (!this.ctx) return;
    // Ascending triad arpeggio: C5 → E5 → G5 → C6
    this.osc(523, 'triangle', 0.12, 0.08);
    setTimeout(() => this.osc(659, 'triangle', 0.12, 0.08), 80);
    setTimeout(() => this.osc(784, 'triangle', 0.12, 0.08), 160);
    setTimeout(() => {
      // Top note — held longer, with a fifth underneath for body.
      this.osc(1047, 'triangle', 0.34, 0.10);
      this.osc(784,  'triangle', 0.30, 0.06);
    }, 240);
  }

  /** Sold-cargo / truck-returned ka-ching — bright noise "shing" + the
   *  two-square coin tones. Adds the metallic transient that pure tones
   *  lacked, so it reads as coins-hitting-counter not a melody. */
  private coinShing() {
    if (!this.ctx) return;
    // Bright shimmer at the front.
    this.trickle(4000, 0.05, 0.05);
    // Classic two-tone coin.
    this.osc(1320, 'square', 0.06, 0.08);
    setTimeout(() => this.osc(1760, 'square', 0.08, 0.07), 50);
  }

  /** Camera zoom in/out — a smooth pitched sweep that matches the direction
   *  the camera is moving. In = rising (`up=true`), out = falling. Soft
   *  filtered-noise body underneath gives it a "movement" feel rather than
   *  a UI beep. */
  private zoomSweep(up: boolean) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const dur = 0.18;
    const from = up ? 360 : 720;
    const to   = up ? 720 : 360;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(from, t);
    o.frequency.exponentialRampToValueAtTime(to, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.06, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(ctx.destination);
    o.start(t);
    o.stop(t + dur + 0.02);
    // Filtered noise hint of "swoosh" underneath, sweeping the same way.
    this.upSweep(from, to, dur, 0.025);
  }

  /** Bandpass-noise sweep helper — pass `from` and `to` in either order
   *  to sweep up or down. Used by plotEnter / plotExit / zoomSweep to add
   *  a "movement" texture under the tonal layer. */
  private upSweep(from: number, to: number, dur: number, peak: number) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(from, t);
    bp.frequency.exponentialRampToValueAtTime(to, t + dur);
    bp.Q.setValueAtTime(2.5, t);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start(t);
    src.stop(t + dur + 0.02);
  }
}
